import Card from "../../ui/Card/Card";
import Button from "../../ui/Button/Button";
import martyImage from "../../images/marty.png";
import cryingMartyImage from "../../images/crying-marty.png";
import IconButton from "../../ui/IconButton/IconButton";
import { Book, Close, ThumbsDown, ThumbsUp } from "../../ui/Icon/Icon";
import styles from "./ArticleFeedbackCard.module.scss";
import Textarea from "../../ui/Textarea/Textarea";
import Label from "../../ui/Label/Label";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { Quality } from "../../__generated__/gql/graphql";
import TileButton from "../../ui/TileButton/TileButton";
import { useMutation } from "urql";
import { graphql } from "../../__generated__/gql";

const AddArticleFloatingFeedbackQuality = graphql(`
  mutation addArticleFloatingFeedbackQuality($id: String!, $quality: Quality!) {
    addArticleFeedback(input: { id: $id, quality: $quality }) {
      id
    }
  }
`);

const AddArticleFloatingFeedbackComment = graphql(`
  mutation addArticleFloatingFeedbackComment($id: String!, $comment: String!) {
    addArticleFeedback(input: { id: $id, comment: $comment }) {
      id
    }
  }
`);

const DismissArticleFeedback = graphql(`
  mutation dismissArticleFeedback($id: String!) {
    dismissArticleFeedback(id: $id) {
      id
      showFloatingFeedback
    }
  }
`);

function QualityStep({
  id,
  onSubmit,
  dismiss,
}: {
  id: string;
  onSubmit: (quality: Quality) => void;
  dismiss: () => void;
}) {
  const [
    addArticleFloatingFeedbackQualityResponse,
    addArticleFloatingFeedbackQuality,
  ] = useMutation(AddArticleFloatingFeedbackQuality);

  const handleSubmit = (quality: Quality) => {
    // Don't wait for the response to update the UI
    void addArticleFloatingFeedbackQuality({ id, quality });

    onSubmit(quality);
  };

  return (
    <Card className={styles.qualityCard}>
      <div className={styles.header}>
        <span>How is the quality of this article?</span>
        <img src={martyImage} alt="" className={styles.headerImage} />
        <IconButton
          IconComponent={Close}
          aria-label="Dismiss"
          onClick={dismiss}
        />
      </div>
      <div className={styles.body}>
        <div className={styles.buttons}>
          <Button
            leftIcon={<ThumbsDown />}
            variant="outline"
            size="small"
            loading={addArticleFloatingFeedbackQualityResponse.fetching}
            onClick={() => handleSubmit(Quality.Bad)}
          >
            Not great
          </Button>
          <Button
            leftIcon={<ThumbsUp />}
            variant="outline"
            size="small"
            loading={addArticleFloatingFeedbackQualityResponse.fetching}
            onClick={() => handleSubmit(Quality.Good)}
          >
            Great
          </Button>
        </div>
      </div>
    </Card>
  );
}

function CommentStep({
  id,
  setStep,
  dismiss,
  quality,
}: {
  id: string;
  setStep: (step: "thanks") => void;
  dismiss: () => void;
  quality: Quality;
}) {
  const [, addArticleFloatingFeedbackComment] = useMutation(
    AddArticleFloatingFeedbackComment,
  );

  const { control, handleSubmit, watch } = useForm<{
    comment: string;
  }>();

  const comment = watch("comment");

  const onSubmit = (data: { comment: string }) => {
    // Don't wait for the response to update the UI
    void addArticleFloatingFeedbackComment({
      id,
      comment: data.comment,
    });

    setStep("thanks");
  };

  return (
    <Card className={styles.commentCard}>
      <div className={styles.header}>
        <span>
          {quality === Quality.Bad
            ? "Sorry to hear that! What could I improve?"
            : "Amazing! What do you like about it?"}
        </span>
        <img
          src={quality === Quality.Bad ? cryingMartyImage : martyImage}
          alt=""
          className={styles.headerImage}
        />
        <IconButton
          IconComponent={Close}
          aria-label="Dismiss"
          onClick={dismiss}
        />
      </div>
      <div className={styles.body}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <Label hideLabel label="Feedback">
            <Controller
              control={control}
              name="comment"
              render={({ field }) => (
                <Textarea placeholder="Enter your feedback" {...field} />
              )}
            />
          </Label>
          <Button
            variant="primary"
            type="submit"
            size="small"
            disabled={!comment}
          >
            Submit
          </Button>
        </form>
      </div>
    </Card>
  );
}

function ThanksStep({
  dismiss,
  quality,
}: {
  dismiss: () => void;
  quality: Quality;
}) {
  return (
    <Card className={styles.thanksCard}>
      <div className={styles.header}>
        <span>Thanks! We will review your feedback.</span>

        <IconButton
          IconComponent={Close}
          aria-label="Dismiss"
          onClick={dismiss}
        />
      </div>
      <div className={styles.body}>
        <p>
          {quality === Quality.Bad
            ? "In the meantime, check out the guide below to learn how you can produce higher quality articles with Macaw"
            : "In the meantime, check out the guide below to learn how you can produce even higher quality articles with Macaw"}
        </p>
        <TileButton
          IconComponent={Book}
          onClick={() =>
            window.open(
              "https://docs.macawhq.com/en/articles/9912541-fine-tuning-articles-for-improved-quality",
              "_blank",
            )
          }
          title="Learn how to improve generation"
          subtitle="Fine-tuning guide for improved output"
        />
      </div>
    </Card>
  );
}

function InternalArticleFeedbackCard({
  id,
  dismiss,
}: {
  id: string;
  dismiss: () => void;
}) {
  const [step, setStep] = useState<"quality" | "comment" | "thanks">("quality");
  const [quality, setQuality] = useState<Quality>(Quality.Good);

  if (step === "quality") {
    return (
      <QualityStep
        id={id}
        dismiss={dismiss}
        onSubmit={(q) => {
          setQuality(q);
          setStep("comment");
        }}
      />
    );
  }

  if (step === "comment") {
    return (
      <CommentStep
        id={id}
        setStep={setStep}
        dismiss={dismiss}
        quality={quality}
      />
    );
  }

  return <ThanksStep dismiss={dismiss} quality={quality} />;
}

function ArticleFeedbackCard({
  id,
  isDismissed,
  articleScrollPercentage,
}: {
  id: string;
  articleScrollPercentage: number;
  isDismissed?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(
    isDismissed ? false : articleScrollPercentage > 0.5,
  );
  const [, dismissArticleFeedback] = useMutation(DismissArticleFeedback);

  const dismiss = () => {
    void dismissArticleFeedback({ id });
    setIsOpen(false);
  };

  useEffect(() => {
    if (articleScrollPercentage > 0.5 && !isDismissed) {
      setIsOpen(true);
    }
  }, [articleScrollPercentage, isDismissed]);

  if (!isOpen) {
    return null;
  }

  return <InternalArticleFeedbackCard id={id} dismiss={dismiss} />;
}

export default ArticleFeedbackCard;
